// frontend/src/components/dashboard/pos/components/CartSection.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Divider,
  Stack,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  List,
  useTheme
} from '@mui/material';
import {
  DeleteOutline,
  ShoppingCart,
  Add,
  Remove,
  MonetizationOn,
  Calculate as CalculatorIcon,
  CreditCard,
  QrCode,
  Devices as DeviceIcon,
  TableRestaurant as TableIcon,
  Person as PersonIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { commonStyles, paymentMethodsGrid, getDeviceConfig } from '../styles/posStyles';
import PaymentMethodButton from '../common/PaymentMethodButton';
import { usePOSSettings } from '../../pages/posconfig/hooks/usePOSSettings';

const MotionDiv = motion.div;

const CartSection = ({
  cartItems,
  onIncreaseQuantity,
  onDecreaseQuantity,
  onRemoveFromCart,
  onClearCart,
  calculateTotal,
  deviceNumber,
  onDeviceNumberChange,
  onProcessOrder,
  onCalculatorToggle,
  selectedPaymentMethod,
  calculatorDialogOpen,
  isOnline,
  isMobile,
  isTablet,
  deviceInfo,
  layoutConfig
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  // ✅ Enhanced POS Settings
  const { 
    settings, 
    calculateCartTotals,
    getCartAddOnsAsArray
  } = usePOSSettings();

  // ✅ Enhanced metadata state
  const [orderMetadata, setOrderMetadata] = useState({
    deviceNumber: deviceNumber || '',
    tableNumber: '',
    customerName: '',
    notes: ''
  });

  // ✅ CRITICAL FIX: Dynamic add-on states - DEFAULT TO FALSE
  const [addOnStates, setAddOnStates] = useState({});

  // ✅ Get enabled metadata fields
  const enabledMetadataFields = Object.entries(settings?.cartMetadata || {})
    .filter(([, config]) => config.enabled && config.visible !== false);

  // ✅ FIXED: Initialize add-on states as UNCHECKED by default
  useEffect(() => {
    if (settings && getCartAddOnsAsArray) {
      const initialStates = {};
      const cartAddOnsArray = getCartAddOnsAsArray();
      
      cartAddOnsArray.forEach(addOn => {
        // ✅ CRITICAL: Always default to FALSE (unchecked)
        initialStates[addOn.id] = false;
      });
      setAddOnStates(initialStates);
      
      console.log('🎯 CartSection add-on states initialized:', {
        availableAddOns: cartAddOnsArray.length,
        enabledInConfig: cartAddOnsArray.filter(a => a.enabled).length,
        defaultCheckedStates: Object.values(initialStates).filter(Boolean).length
      });
    }
  }, [settings, getCartAddOnsAsArray]);

  // Update legacy props when metadata changes
  useEffect(() => {
    if (onDeviceNumberChange) {
      onDeviceNumberChange({ target: { value: orderMetadata.deviceNumber } });
    }
  }, [orderMetadata.deviceNumber, onDeviceNumberChange]);

  // Don't show cart section on mobile
  if (isMobile) {
    return null;
  }

  // ✅ ADD ERROR BOUNDARY - Check if settings are loaded
  if (!settings) {
    console.log('⏳ CartSection: Waiting for settings to load...');
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          width: 380,
          minWidth: 380,
          maxWidth: 380,
          borderLeft: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading POS settings...
        </Typography>
      </Paper>
    );
  }

  // Get device-specific configuration
  const deviceConfig = getDeviceConfig(deviceInfo?.type, deviceInfo?.screenSize);
  const cartWidth = layoutConfig?.cartWidth || deviceConfig.cartWidth;
  const spacing = layoutConfig?.spacing || deviceConfig.spacing;
  const fontSize = layoutConfig?.fontSize || deviceConfig.fontSize;
  const cartItemHeight = deviceConfig.cartItemHeight;

  // ✅ SAFE: Calculate subtotal
  const subtotal = cartItems.reduce((sum, item) => {
    const itemPrice = parseFloat(item.finalPrice || item.price || 0);
    const itemQuantity = parseInt(item.quantity || 0);
    return sum + (itemPrice * itemQuantity);
  }, 0);
  
  // ✅ CRITICAL: Calculate totals using ONLY user-checked add-ons
  const totals = settings ? 
    calculateCartTotals(subtotal, addOnStates) : 
    calculateTotal();

  // ✅ FIXED: Get enabled cart add-ons for DISPLAY
  const enabledAddOns = settings?.cartAddOns?.filter(addOn => 
    addOn.enabled && addOn.visible // Show if enabled AND visible in config
  ) || [];

  const debugShowAddOns = settings?.cartAddOns || [];
  console.log('🔧 DEBUG: Forcing add-ons display:', debugShowAddOns);

 

  console.log('🎯 Cart add-ons DEBUG:', {
    settingsExist: !!settings,
    totalCartAddOns: settings?.cartAddOns?.length || 0,
    allCartAddOns: settings?.cartAddOns?.map(a => ({ 
      id: a.id, 
      name: a.name, 
      enabled: a.enabled, 
      visible: a.visible 
    })) || [],
    enabledAndVisible: enabledAddOns.length,
    enabledAddOns: enabledAddOns.map(a => ({ 
      id: a.id, 
      name: a.name, 
      enabled: a.enabled, 
      visible: a.visible 
    })),
    currentlyChecked: Object.values(addOnStates).filter(Boolean).length,
    addOnStates: addOnStates
  });

  // ✅ Get enabled payment methods
  const enabledPaymentMethods = Object.entries(settings?.paymentMethods || {})
    .filter(([, method]) => method.enabled && method.visible && !method.comingSoon)
    .sort(([, a], [, b]) => a.order - b.order);

  // ✅ ENHANCED: Handle add-on toggle (user manual opt-in/out)
  const handleAddOnToggle = (addOnId, checked) => {
    console.log(`🔄 User toggled add-on "${addOnId}": ${checked}`);
    
    setAddOnStates(prev => ({
      ...prev,
      [addOnId]: checked
    }));
    
    // ✅ Log the change for debugging
    const addOnsArray = getCartAddOnsAsArray();
    const addOn = addOnsArray.find(a => a.id === addOnId);
    if (addOn) {
      console.log(`${checked ? '✅' : '❌'} "${addOn.name}" ${checked ? 'applied' : 'removed'} by user`);
    }
  };

  // ✅ Handle metadata change
  const handleMetadataChange = (fieldName, value) => {
    setOrderMetadata(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // ✅ Get field icon
  const getFieldIcon = (fieldName) => {
    switch (fieldName) {
      case 'deviceNumber':
        return <DeviceIcon fontSize="small" />;
      case 'tableNumber':
        return <TableIcon fontSize="small" />;
      case 'customerName':
        return <PersonIcon fontSize="small" />;
      case 'notes':
        return <NotesIcon fontSize="small" />;
      default:
        return <DeviceIcon fontSize="small" />;
    }
  };

  // ✅ Get payment method icon
  const getPaymentIcon = (iconName) => {
    switch (iconName) {
      case 'MonetizationOn':
        return MonetizationOn;
      case 'Calculate':
        return CalculatorIcon;
      case 'QrCode':
        return QrCode;
      case 'CreditCard':
        return CreditCard;
      default:
        return MonetizationOn;
    }
  };

  console.log('🎯 Cart metadata for display:', {
    totalConfigured: Object.keys(settings?.cartMetadata || {}).length,
    enabledAndVisible: enabledMetadataFields.length,
    fields: enabledMetadataFields.map(([name, config]) => ({
      name,
      enabled: config.enabled,
      visible: config.visible,
      label: config.label
    }))
  });

  return (
    <Paper 
      elevation={0} 
      className="cart-section"
      sx={{ 
        width: cartWidth,
        minWidth: cartWidth,
        maxWidth: cartWidth,
        borderLeft: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        backdropFilter: 'blur(10px)',
        borderRadius: { lg: '16px 0 0 16px' },
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        overflow: 'hidden',
        flexShrink: 0
      }}
    >
      {/* Cart Header */}
      <Box sx={{
        ...commonStyles.headerSection,
        p: spacing * 1,
        minHeight: isTablet ? 36 : 44,
        maxHeight: isTablet ? 40 : 48
      }}>
        <Typography variant="h6" sx={{ 
          fontWeight: 600,
          fontSize: fontSize.large,
          lineHeight: 1.2
        }}>
          {t('pos.currentOrder')}
        </Typography>
        <IconButton 
          color="inherit" 
          onClick={onClearCart}
          disabled={cartItems.length === 0}
          size="small"
          sx={{
            width: isTablet ? 28 : 32,
            height: isTablet ? 28 : 32
          }}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Box>
      
      <Divider />
      
      {/* Cart Items - Scrollable */}
      <Box sx={{ 
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        maxHeight: layoutConfig?.cartHeight || '50vh',
        ...commonStyles.scrollbar
      }}
      className="cart-items-container"
      >
        {cartItems.length === 0 ? (
          <Box sx={{ p: spacing * 2, textAlign: 'center' }}>
            <ShoppingCart sx={{ 
              fontSize: isTablet ? 36 : 48, 
              color: '#667eea', 
              opacity: 0.4, 
              mb: spacing 
            }} />
            <Typography color="#667eea" sx={{
              fontSize: fontSize.medium
            }}>
              {t('pos.cartEmpty')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              mt: 0.5,
              fontSize: fontSize.small
            }}>
              {t('pos.cartEmptyDesc')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {cartItems.map((item, index) => (
              <MotionDiv
                key={`cart-item-${item.id}-${item.selectedVariation?.variation_id || 'base'}-${item.uniqueId || index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <Box 
                  className="cart-item"
                  sx={{
                    ...commonStyles.cartItem,
                    minHeight: `${cartItemHeight}px`,
                    px: spacing,
                    py: spacing * 0.75
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 500, 
                      color: '#333',
                      fontSize: fontSize.medium,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.displayName || item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#667eea', 
                      fontWeight: 600,
                      fontSize: fontSize.small
                    }}>
                      {settings?.defaultSettings?.currencySymbol || '$'}{(parseFloat(item.finalPrice || item.price || 0) * (item.quantity || 1)).toFixed(settings?.defaultSettings?.decimalPlaces || 2)}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      fontSize: fontSize.small
                    }}>
                      {settings?.defaultSettings?.currencySymbol || '$'}{parseFloat(item.finalPrice || item.price || 0).toFixed(settings?.defaultSettings?.decimalPlaces || 2)} each
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={0.25}>
                    <IconButton 
                      size="small" 
                      onClick={() => onDecreaseQuantity(item.uniqueId || item.id)}
                      className="quantity-button"
                      sx={{
                        ...commonStyles.quantityButton,
                        width: isTablet ? 24 : 28,
                        height: isTablet ? 24 : 28,
                        minWidth: isTablet ? 24 : 28
                      }}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <Typography sx={{ 
                      fontWeight: 600, 
                      minWidth: '20px', 
                      textAlign: 'center',
                      fontSize: fontSize.small,
                      mx: 0.5
                    }}>
                      {item.quantity || 1}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => onIncreaseQuantity(item.uniqueId || item.id)}
                      className="quantity-button"
                      sx={{
                        ...commonStyles.quantityButton,
                        width: isTablet ? 24 : 28,
                        height: isTablet ? 24 : 28,
                        minWidth: isTablet ? 24 : 28
                      }}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                    <IconButton 
                      edge="end" 
                      onClick={() => onRemoveFromCart(item.uniqueId || item.id)}
                      size="small"
                      sx={{ 
                        ml: 0.5, 
                        color: 'text.secondary',
                        width: isTablet ? 24 : 28,
                        height: isTablet ? 24 : 28
                      }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </MotionDiv>
            ))}
          </List>
        )}
      </Box>
      
      {/* ✅ FIXED: Dynamic Add-Ons Section - Show only enabled add-ons */}
      {enabledAddOns.length > 0 && (
        <Box sx={{ 
          flexShrink: 0,
          p: spacing, 
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'rgba(102, 126, 234, 0.02)'
        }}>
          {/* ✅ Add-ons header */}
          <Typography variant="subtitle2" sx={{ 
            mb: spacing * 0.75, 
            fontWeight: 500, 
            color: '#667eea',
            fontSize: fontSize.small
          }}>
            {t('pos.addOns') || 'Add-ons'}:
          </Typography>
          
          <Stack spacing={spacing}>
            {/* ✅ Show ONLY enabled and visible add-ons */}
            {enabledAddOns.map(addOn => {
              const isChecked = addOnStates[addOn.id] || false;
              
              return (
                <FormControlLabel
                  key={addOn.id}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => handleAddOnToggle(addOn.id, e.target.checked)}
                      size="small"
                      sx={{
                        color: '#667eea',
                        '&.Mui-checked': { color: '#667eea' }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ 
                        color: isChecked ? '#333' : '#666',
                        fontSize: fontSize.small,
                        fontWeight: isChecked ? 600 : 400
                      }}>
                        {addOn.name} ({addOn.type === 'fixed' ? 
                          `${settings?.defaultSettings?.currencySymbol || '$'}${parseFloat(addOn.value || 0).toFixed(settings?.defaultSettings?.decimalPlaces || 2)}` : 
                          `${parseFloat(addOn.value || 0)}%`})
                      </Typography>
                      
                      {/* ✅ Show when add-on is applied */}
                      {isChecked && (
                        <Typography variant="caption" sx={{ 
                          color: addOn.type === 'discount' ? '#ef4444' : '#10b981',
                          fontSize: fontSize.small * 0.85,
                          fontWeight: 600,
                          display: 'block'
                        }}>
                          {addOn.type === 'discount' ? '💸 Discount Applied' : '💰 Charge Applied'}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              );
            })}
          </Stack>
        </Box>
      )}
      
      {/* ✅ Dynamic Metadata Fields Section */}
      {enabledMetadataFields.length > 0 && (
        <Box sx={{ 
          flexShrink: 0,
          p: spacing, 
          borderTop: `1px solid ${theme.palette.divider}`
        }}>
          <Stack spacing={spacing}>
            {enabledMetadataFields.map(([fieldName, fieldConfig], index) => (
              <Box key={`metadata-field-${fieldName}-${index}`}>
                <Typography variant="subtitle2" sx={{ 
                  mb: 0.5, 
                  fontWeight: 500, 
                  color: '#667eea',
                  fontSize: fontSize.small
                }}>
                  {fieldConfig.label}:
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={fieldConfig.placeholder}
                  value={orderMetadata[fieldName] || ''}
                  onChange={(e) => handleMetadataChange(fieldName, e.target.value)}
                  required={fieldConfig.required}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {getFieldIcon(fieldName)}
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: isTablet ? '6px' : '8px',
                      fontSize: fontSize.small,
                      '& input': {
                        py: isTablet ? 0.75 : 1
                      }
                    }
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      
      {/* ✅ ENHANCED: Totals Summary with proper add-on calculation */}
      <Box sx={{ 
        flexShrink: 0,
        p: spacing, 
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.default,
        maxHeight: '45vh',
        overflow: 'visible'
      }}>
        <Stack spacing={isTablet ? 0.25 : 0.5} sx={{ mb: spacing * 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ 
              color: '#555',
              fontSize: fontSize.small
            }}>
              {t('pos.subtotal')}:
            </Typography>
            <Typography sx={{ 
              fontWeight: 500, 
              color: '#333',
              fontSize: fontSize.small
            }}>
              {settings?.defaultSettings?.currencySymbol || '$'}{(parseFloat(totals.subtotal || 0)).toFixed(settings?.defaultSettings?.decimalPlaces || 2)}
            </Typography>
          </Box>
          
          {/* ✅ ENHANCED: Show only CHECKED add-ons in totals */}
          {enabledAddOns.map((addOn, index) => {
            const isChecked = addOnStates[addOn.id];
            if (!isChecked) return null;
            
            const amount = parseFloat(totals.addOns?.[addOn.id] || 0);
            const isDiscount = addOn.type === 'discount';
            
            return (
              <Box key={`total-line-${addOn.id}-${index}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ 
                  color: '#555',
                  fontSize: fontSize.small
                }}>
                  {addOn.name}:
                </Typography>
                <Typography sx={{ 
                  fontWeight: 500, 
                  color: isDiscount ? '#ef4444' : '#333',
                  fontSize: fontSize.small
                }}>
                  {isDiscount ? '-' : ''}{settings?.defaultSettings?.currencySymbol || '$'}{Math.abs(amount).toFixed(settings?.defaultSettings?.decimalPlaces || 2)}
                </Typography>
              </Box>
            );
          })}
          
          <Divider sx={{ my: 0.5 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: '#333',
              fontSize: fontSize.medium
            }}>
              {t('pos.total')}:
            </Typography>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: '#667eea',
              fontSize: fontSize.medium
            }}>
              {settings?.defaultSettings?.currencySymbol || '$'}{(parseFloat(totals.total || 0)).toFixed(settings?.defaultSettings?.decimalPlaces || 2)}
            </Typography>
          </Box>
        </Stack>
        
        {/* ✅ Dynamic Payment Methods */}
        <Typography variant="subtitle2" sx={{ 
          mb: spacing * 0.75, 
          fontWeight: 500, 
          color: '#667eea',
          fontSize: fontSize.small
        }}>
          {t('pos.paymentMethods')}:
        </Typography>

        <Box 
          className="payment-grid"
          sx={{
            ...paymentMethodsGrid,
            gap: spacing * 0.5,
            '& > *': {
              minHeight: isTablet ? 
                (deviceInfo?.screenSize === 'small' ? '50px' : 
                 deviceInfo?.screenSize === 'medium' ? '55px' : '60px') 
                : '70px'
            }
          }}
        >
          {enabledPaymentMethods.map(([methodId, method], index) => {
            const IconComponent = getPaymentIcon(method.icon);
            return (
              <PaymentMethodButton
                key={`payment-btn-${methodId}-${index}`}
                icon={IconComponent}
                label={method.name}
                onClick={() => {
                  console.log('🔘 Payment method clicked:', methodId, method.name);
                  
                  if (methodId === 'calculate_change' || methodId === 'calculateChange') {
                    console.log('🧮 Opening calculator for change calculation');
                    onCalculatorToggle();
                  } else {
                    console.log('💰 Processing direct payment:', methodId);
                    onProcessOrder(methodId);
                  }
                }}
                disabled={cartItems.length === 0 || !isOnline}
                selected={selectedPaymentMethod === methodId || (methodId === 'calculate_change' && calculatorDialogOpen)}
                deviceInfo={deviceInfo}
                isTablet={isTablet}
              />
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default CartSection;